import dayjs from "dayjs";

import { TagList } from "@/shared/components/TagList";
import { UserLink } from "@/shared/components/UserLink";
import {
  cellStyles,
  createColumnHelper,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { RobotoPrincipal, RobotoPrincipalType } from "@/shared/principal";
import { nanoSecToDate } from "@/shared/time";

import { EventRecord } from "./EventRecord";

const columnHelper = createColumnHelper<EventRecord>();

export function formatNanoTimestamp(value: bigint | undefined | null): string {
  if (value === undefined || value === null) {
    return "";
  }
  try {
    return dayjs(nanoSecToDate(BigInt(value))).format("MMM D, YYYY h:mm A");
  } catch {
    return "";
  }
}

export const eventsTableColumns = [
  columnHelper.accessor("event_id", {
    headerName: "ID",
    width: 180,
    cellClass: cellStyles.monoCell,
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 1,
    sortable: true,
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 3,
  }),
  columnHelper.accessor("start_time", {
    headerName: "Start",
    width: 200,
    sortable: true,
    cellClass: cellStyles.monoCell,
    valueFormatter: (p) => formatNanoTimestamp(p.value),
  }),
  columnHelper.accessor("end_time", {
    headerName: "End",
    width: 200,
    sortable: true,
    cellClass: cellStyles.monoCell,
    valueFormatter: (p) => formatNanoTimestamp(p.value),
  }),
  columnHelper.accessor("created", {
    headerName: "Created",
    width: 150,
    sortable: true,
    valueFormatter: (p) => {
      if (!p.value) {
        return "";
      }
      const value = dayjs(p.value);
      return dayjs().diff(value, "day") >= 7
        ? value.format("MMM D, YYYY")
        : value.fromNow();
    },
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    width: 150,
    sortable: true,
    cellRenderer: (p: ICellRendererParams<EventRecord, string>) => {
      const value = p.value ?? "";
      const principal = RobotoPrincipal.fromString(value);
      if (principal?.ptype === RobotoPrincipalType.User) {
        return (
          <UserLink
            userId={principal.unqualifiedId}
            linkSx={{ fontSize: "var(--font-size-xs)" }}
          />
        );
      }
      return <span>{principal?.unqualifiedId ?? value}</span>;
    },
  }),
  columnHelper.accessor("modified", {
    headerName: "Modified",
    width: 150,
    sortable: true,
    valueFormatter: (p) => {
      if (!p.value) {
        return "";
      }
      const value = dayjs(p.value);
      return dayjs().diff(value, "day") >= 7
        ? value.format("MMM D, YYYY")
        : value.fromNow();
    },
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    width: 150,
    sortable: true,
    cellRenderer: (p: ICellRendererParams<EventRecord, string>) => {
      const value = p.value ?? "";
      const principal = RobotoPrincipal.fromString(value);
      if (principal?.ptype === RobotoPrincipalType.User) {
        return (
          <UserLink
            userId={principal.unqualifiedId}
            linkSx={{ fontSize: "var(--font-size-xs)" }}
          />
        );
      }
      return <span>{principal?.unqualifiedId ?? value}</span>;
    },
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    cellClass: cellStyles.tagsCell,
    // Object types require a string formatting for accessibility
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: (p: ICellRendererParams<EventRecord, string[]>) => (
      <TagList tags={p.value ?? []} maxDisplayCount={3} />
    ),
  }),
];
